import { Injectable } from '@angular/core';

/**
 * Prints a PDF by streaming the bytes into a hidden, same-origin iframe and invoking the native
 * print dialog. Using an object URL (rather than the browser's PDF plugin print path) keeps the
 * behaviour consistent across sources — URL, Blob, base64, or ArrayBuffer.
 */
@Injectable({ providedIn: 'root' })
export class PdfPrintService {
  /** Print from in-memory bytes. */
  print(data: Uint8Array | ArrayBuffer): Promise<void> {
    // `Uint8Array<ArrayBufferLike>` (TS 5.7+) isn't structurally a `BlobPart`; the bytes are always
    // a valid blob source at runtime, so assert rather than copy the (potentially large) buffer.
    const blob = new Blob([data as BlobPart], { type: 'application/pdf' });
    return this.printObjectUrl(URL.createObjectURL(blob), true);
  }

  /** Print directly from a (same-origin) URL. */
  printUrl(url: string): Promise<void> {
    return this.printObjectUrl(url, false);
  }

  private printObjectUrl(url: string, revoke: boolean): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.setAttribute('aria-hidden', 'true');

      const cleanup = (): void => {
        if (revoke) {
          URL.revokeObjectURL(url);
        }
        // Defer removal so the print job can read the frame contents.
        setTimeout(() => iframe.remove(), 1000);
      };

      iframe.onload = () => {
        try {
          const win = iframe.contentWindow;
          if (!win) {
            throw new Error('Print frame has no content window.');
          }
          win.focus();
          win.print();
          resolve();
        } catch (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
        } finally {
          cleanup();
        }
      };
      iframe.onerror = () => {
        cleanup();
        reject(new Error('Failed to load the document for printing.'));
      };

      iframe.src = url;
      document.body.appendChild(iframe);
    });
  }
}
